import clsx from 'clsx';
import type * as React from 'react';

import { Icon } from '@gitbook/spine-icons';
import { Alert, Badge, FieldGroup, IconTile, Label, Link } from '@gitbook/spine-react';

/**
 * Identity of the OAuth client requesting access.
 */
export function OAuthClientIdentity(props: {
    client: {
        name: string;
        logoUri: string | null;
        uri: string | null;
        tosUri?: string | null;
        policyUri?: string | null;
    };
    redirectUri: string;
    verified: boolean;
}): React.ReactElement {
    const { client, redirectUri, verified } = props;

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-start gap-3">
                <IconTile size={48} className="shrink-0 border-base bg-neutral-ui text-muted">
                    {client.logoUri ? (
                        <img
                            alt=""
                            src={client.logoUri}
                            className="size-full rounded-lg object-cover"
                        />
                    ) : (
                        <Icon.Key className="size-5" />
                    )}
                </IconTile>
                <div className="flex min-w-0 flex-col gap-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold">{client.name}</span>
                        {verified ? (
                            <Badge
                                kind="success"
                                size="small"
                                thin
                                icon={Icon.CheckCircleFill}
                                label="Verified"
                            />
                        ) : (
                            <Badge
                                kind="warning"
                                size="small"
                                thin
                                icon={Icon.WarningCircleFill}
                                label="Unverified"
                            />
                        )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <ClientLink href={client.uri} label="Website" />
                        <ClientLink href={client.tosUri} label="Terms" />
                        <ClientLink href={client.policyUri} label="Privacy" />
                    </div>
                </div>
            </div>

            <FieldGroup orientation="horizontal">
                <Label>Redirect URI</Label>
                <code
                    className={clsx(
                        'font-mono text-ui-small break-all',
                        !verified && 'text-warning'
                    )}
                >
                    {redirectUri}
                </code>
            </FieldGroup>

            {verified ? null : (
                <Alert
                    kind="warning"
                    title="GitBook has not verified this client"
                    details="After you allow, an authorization code that grants access to your account will be sent to the URI above. Make sure you trust it."
                />
            )}
        </div>
    );
}

function ClientLink(props: {
    href: string | null | undefined;
    label: string;
}): React.ReactElement | null {
    if (!props.href) {
        return null;
    }
    return (
        <Link
            href={props.href}
            target="_blank"
            rel="noreferrer"
            kind="inherit"
            trailingIcon={Icon.ArrowUpRight}
            className="text-ui-small text-muted hover:text-base"
        >
            {props.label}
        </Link>
    );
}
